// Add a store by address (Phase 4b plan §2.5): find it, show what was found, save on "yes". The
// server looks the address up again on save and keeps those coordinates (retailers cannot type them).
import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { dashboardAPI } from '../../../services/dashboard-api';

const COUNTRY_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });
const countryName = (code) => COUNTRY_NAMES.of(code) || code;

export default function StoreForm({ retailerId, countries, onSaved, onCancel }) {
  const [address, setAddress] = useState({
    name: '',
    street: '',
    postal_code: '',
    city: '',
    country_code: countries?.[0] || 'NL',
  });
  const [found, setFound] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setAddress({ ...address, [key]: e.target.value });

  const find = async () => {
    setBusy(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await dashboardAPI.lookupStore(address, retailerId);
      setFound(res.found);
      setNotFound(!res.found);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await dashboardAPI.createStore(address, retailerId);
      onSaved(res.store);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const ready = address.name.trim() && address.street.trim() && (address.postal_code.trim() || address.city.trim());

  return (
    <Stack spacing={1.5} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
      {!found ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField label="Store name" value={address.name} onChange={set('name')} sx={{ gridColumn: { sm: 'span 2' } }} />
            <TextField
              label="Street and number"
              value={address.street}
              onChange={set('street')}
              sx={{ gridColumn: { sm: 'span 2' } }}
            />
            <TextField label="Postcode" value={address.postal_code} onChange={set('postal_code')} />
            <TextField label="Town" value={address.city} onChange={set('city')} />
            {countries?.length > 1 && (
              <TextField select label="Country" value={address.country_code} onChange={set('country_code')}>
                {countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {countryName(c)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          {notFound && (
            <Alert severity="warning">
              We could not find this address. Check the street, the number and the postcode. Still not found?{' '}
              <Link href="mailto:info@handoffleads.com">Contact HandOff</Link> and we add the store for you.
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onCancel} sx={{ minHeight: 44 }}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={find} disabled={busy || !ready} sx={{ minHeight: 44 }}>
              {busy ? <CircularProgress size={18} /> : 'Find the store'}
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Box>
            <Typography>
              We found: {[found.address_line, [found.postal_code, found.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
              {found.region_name ? `, ${found.region_name}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Is this your store? The distance is measured from here.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
            <Button onClick={() => setFound(null)} sx={{ minHeight: 44 }}>
              No, change the address
            </Button>
            <Button variant="outlined" onClick={save} disabled={busy} sx={{ minHeight: 44 }}>
              {busy ? <CircularProgress size={18} /> : 'Yes, save store'}
            </Button>
          </Stack>
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
